import React, { type ComponentType } from 'react';
import clsx from 'clsx';

interface SecondaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
  state?: 'default' | 'disabled' | 'loading' | 'selected';
  size?: 'md' | 'sm';
  icon: ComponentType<React.SVGProps<SVGSVGElement>> | null;
}

export const SecondaryButton = React.forwardRef<HTMLButtonElement, SecondaryButtonProps>(
  (
    {
      text = 'Primary',
      size = 'md',
      state = 'default',
      className = '',
      icon: Icon,
      disabled,
      ...buttonProps
    },
    ref,
  ) => {
    const isDisabled = disabled || state === 'disabled' || state === 'loading';

    return (
      <button
        ref={ref}
        className={clsx(
          'flex items-center justify-center overflow-hidden rounded-md border outline outline-offset-[-1px] outline-transparent transition-all',
          {
            'gap-2 px-[11px] py-[7px]': size === 'md',
            'gap-1 px-[7px] py-[5px]': size === 'sm',
            'cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-200':
              state === 'disabled',
            'cursor-progress border-neutral-200 bg-neutral-50 text-neutral-200':
              state === 'loading',
            'bg-neutral-10 cursor-pointer text-neutral-600 hover:bg-neutral-50 focus-visible:border-indigo-500 active:border-indigo-500 active:bg-neutral-50':
              state === 'default' || state === 'selected',
            'border-neutral-300': state === 'default',
            'border-indigo-500': state === 'selected',
          },
          className,
        )}
        disabled={isDisabled}
        {...buttonProps}
      >
        {Icon && (
          <>
            <Icon
              className={clsx('', {
                'h-5 w-5': size === 'md',
                'h-4 w-4': size === 'sm',
              })}
            />
          </>
        )}
        <span
          className={clsx('', {
            'text-button-md': size === 'md',
            'text-button-sm': size === 'sm',
          })}
        >
          {text}
        </span>
      </button>
    );
  },
);

SecondaryButton.displayName = 'SecondaryButton';
